import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { JobCard } from "../components/JobCard";
import { EmptyState } from "../components/EmptyState";
import { CvSection } from "../components/CvSection";
import { AccountSection } from "../components/AccountSection";
import { AlertsTab } from "../components/AlertsTab";
import { NotificationsTab, NOTIFICATIONS_CHANGED } from "../components/candidate/NotificationsTab";
import { SavedSearchesTab } from "../components/candidate/SavedSearchesTab";
import { ReferralCard } from "../components/ReferralCard";
import { OnboardingChecklist } from "../components/OnboardingChecklist";
import { ApplicationsList } from "../components/candidate/ApplicationsList";
import { ProfileForm } from "../components/candidate/ProfileForm";
import { LinkedinImportCard } from "../components/candidate/LinkedinImportCard";
import { ProfileOptimizer } from "../components/candidate/ProfileOptimizer";
import { CoverLetterDialog } from "../components/candidate/CoverLetterDialog";
import { CandidateSidebar } from "../components/candidate/CandidateSidebar";
import { CandidateStatsCards } from "../components/candidate/CandidateStatsCards";
import { RecommendationsSection } from "../components/candidate/RecommendationsSection";
import { ActivityFeed } from "../components/candidate/ActivityFeed";
import { ApplicationsChart } from "../components/candidate/ApplicationsChart";
import { UpcomingInterviews } from "../components/candidate/UpcomingInterviews";
import { InterviewPrepDialog } from "../components/candidate/InterviewPrepDialog";
import { Badge } from "../components/ui/badge";
import { Heart, Crown } from "lucide-react";

export default function CandidateDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [saved, setSaved] = useState([]);
  const [profile, setProfile] = useState(null);
  const [meta, setMeta] = useState(null);
  const [generatingLetter, setGeneratingLetter] = useState(null);
  const [letterDialog, setLetterDialog] = useState(null);
  const [copied, setCopied] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [linkedinImporting, setLinkedinImporting] = useState(false);
  const [linkedinPreview, setLinkedinPreview] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [matchesCount, setMatchesCount] = useState(0);
  const [prepInterview, setPrepInterview] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTabState] = useState(() => searchParams.get("tab") || "overview");
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  // Separador refletido no URL (?tab=notifications) — permite links diretos, ex. a partir do sino
  const setActiveTab = (t) => {
    setActiveTabState(t);
    const next = new URLSearchParams(searchParams);
    if (t && t !== "overview") next.set("tab", t); else next.delete("tab");
    setSearchParams(next, { replace: true });
  };
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== activeTab) setActiveTabState(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  useEffect(() => {
    const refresh = () => api.get("/notifications/unread-count").then((r) => setUnreadNotifs(r.data.unread || 0)).catch(() => {});
    refresh();
    window.addEventListener(NOTIFICATIONS_CHANGED, refresh);
    return () => window.removeEventListener(NOTIFICATIONS_CHANGED, refresh);
  }, []);

  useEffect(() => {
    if (user === false) navigate("/auth");
    if (user && user.role !== "candidate") navigate("/");
    if (user && user.role === "candidate") {
      setProfile({
        name: user.name || "", phone: user.phone || "", district: user.district || "",
        concelho: user.concelho || "", headline: user.headline || "", about: user.about || "",
      });
      api.get("/meta").then((r) => setMeta(r.data)).catch((e) => console.error("meta:", e));
      api.get("/candidate/applications").then((r) => setApps(r.data.applications)).catch((e) => console.error("applications:", e));
      api.get("/candidate/saved").then((r) => setSaved(r.data.jobs)).catch((e) => console.error("saved:", e));
      api.get("/candidate/alerts").then((r) => setAlerts(r.data.alerts || [])).catch(() => {});
      api.get("/me/referrals").then((r) => setReferrals(r.data)).catch(() => {});
    }
  }, [user, navigate]);

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await api.put("/candidate/profile", profile);
      toast.success("Perfil atualizado");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const requestLetter = async (appId, force = false) => {
    setGeneratingLetter(appId);
    try {
      const url = `/candidate/applications/${appId}/cover-letter${force ? "?force=true" : ""}`;
      const { data } = await api.post(url);
      setApps((prev) => prev.map((a) => (a.id === appId ? { ...a, cover_letter: data.cover_letter } : a)));
      return data.cover_letter;
    } catch (e) {
      toast.error(formatErr(e));
      return null;
    } finally { setGeneratingLetter(null); }
  };

  const openLetter = async (app) => {
    if (app.cover_letter) {
      setLetterDialog({ appId: app.id, jobTitle: app.job_title, text: app.cover_letter });
      return;
    }
    const text = await requestLetter(app.id);
    if (text) setLetterDialog({ appId: app.id, jobTitle: app.job_title, text });
  };

  const regenLetter = async () => {
    if (!letterDialog) return;
    const text = await requestLetter(letterDialog.appId, true);
    if (text) {
      setLetterDialog((prev) => ({ ...prev, text }));
      toast.success("Nova versão gerada!");
    }
  };

  const copyLetter = () => {
    if (!letterDialog?.text) return;
    navigator.clipboard.writeText(letterDialog.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const optimizeProfile = async () => {
    setOptimizing(true);
    try {
      const { data } = await api.post("/candidate/profile/optimize");
      setSuggestions(data.suggestions);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setOptimizing(false); }
  };

  const handleLinkedinImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLinkedinImporting(true);
    setLinkedinPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/candidate/linkedin/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLinkedinPreview(data);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLinkedinImporting(false); }
  };

  const applyLinkedinImport = async () => {
    if (!linkedinPreview) return;
    const fields = ["name", "headline", "about", "phone", "district"];
    const updates = fields.reduce((acc, key) => {
      if (linkedinPreview[key]) acc[key] = linkedinPreview[key];
      return acc;
    }, {});
    try {
      await api.put("/candidate/profile", updates);
      setProfile((prev) => ({ ...prev, ...updates }));
      setLinkedinPreview(null);
      toast.success("Perfil importado do LinkedIn com sucesso!");
    } catch (err) { toast.error(formatErr(err)); }
  };

  const isPremium = user?.premium_until && new Date(user.premium_until) > new Date();

  const profileComplete = useMemo(() => Boolean(
    profile?.name && profile?.phone && profile?.district && profile?.concelho && profile?.headline
  ), [profile]);

  const activeApps = useMemo(
    () => apps.filter((a) => a.status !== "rejeitada" && a.status !== "aceite").length,
    [apps]
  );

  const stats = useMemo(() => ({
    applications: apps.length,
    active: activeApps,
    saved: saved.length,
    alerts: alerts.length,
    matches: matchesCount,
    referrals: referrals?.referral_count || 0,
  }), [apps, activeApps, saved, alerts, referrals, matchesCount]);

  const sidebarCounts = useMemo(() => ({
    applications: apps.length || undefined,
    saved: saved.length || undefined,
    alerts: alerts.length || undefined,
    notifications: unreadNotifs || undefined,
  }), [apps.length, saved.length, alerts.length, unreadNotifs]);

  if (!user || !profile) return <div className="min-h-screen bg-[#FAFAFA]"><Navbar /></div>;

  const hasCv = Boolean(user.candidate_has_cv);
  const hasApp = apps.length > 0;
  const hasAlert = alerts.length > 0;
  const referralCount = referrals?.referral_count || 0;

  const onboardingItems = [
    { id: "account", done: true, title: "Conta criada",
      description: "Boas-vindas! A tua conta está ativa e o consentimento RGPD foi registado." },
    { id: "profile", done: profileComplete,
      title: "Completar o perfil",
      description: "Nome, telefone, distrito, concelho e headline — para as empresas te encontrarem.",
      actionLabel: "Completar", onAction: () => setActiveTab("profile") },
    { id: "cv", done: hasCv,
      title: "Carregar o CV em PDF",
      description: "Fica em armazenamento privado. A IA analisa competências, pontos fortes e sugestões.",
      actionLabel: "Carregar CV", onAction: () => setActiveTab("profile") },
    { id: "apply", done: hasApp,
      title: "Candidatar-me à primeira vaga",
      description: "Um clique por vaga e vês o match score 0-100 antes de te candidatares.",
      actionLabel: "Explorar vagas", onAction: () => navigate("/empregos") },
    { id: "alert", done: hasAlert,
      title: "Criar um alerta de emprego",
      description: "Guarda a tua pesquisa (distrito + categoria) e recebemos as novidades por ti.",
      actionLabel: "Criar alerta", onAction: () => setActiveTab("alerts") },
    { id: "referral", done: referralCount >= 3,
      title: "Convidar 3 amigos e ganhar Premium",
      description: `Já convidaste ${referralCount} de 3. Com 3 registados ganhas 30 dias de IA ilimitada e badge.`,
      actionLabel: "Ver código", onAction: () => setActiveTab("profile") },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <Navbar />

      <div className="flex-1 lg:flex lg:items-start">
        <CandidateSidebar
          activeTab={activeTab}
          onChange={setActiveTab}
          counts={sidebarCounts}
          isPremium={isPremium}
          onLogout={logout}
        />

        <main className="flex-1 min-w-0 px-4 sm:px-8 py-6 sm:py-10 lg:py-10 lg:pl-10 lg:pr-10">
          {/* Header */}
          <div className="mb-6">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600 mb-2">Painel de Candidato</p>
            <div className="flex flex-wrap items-center gap-3">
              <h1 data-testid="candidate-dashboard-title" className="font-heading font-black tracking-tighter text-2xl sm:text-3xl text-slate-900">
                Olá, {user.name?.split(" ")[0]}
              </h1>
              {isPremium && (
                <Badge data-testid="candidate-premium-badge" className="bg-amber-400 text-slate-950 hover:bg-amber-400">
                  <Crown className="w-3 h-3 mr-1" /> Premium IA
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Bem-vindo/a de volta. Aqui está o teu progresso e as melhores oportunidades.
            </p>
          </div>

          {/* OVERVIEW TAB */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              <CandidateStatsCards stats={stats} onCardClick={(k) => {
                if (k === "applications") setActiveTab("applications");
                else if (k === "saved") setActiveTab("saved");
                else if (k === "alerts") setActiveTab("alerts");
                else if (k === "matches") setActiveTab("recommendations");
                else if (k === "referrals") setActiveTab("profile");
              }} />

              <OnboardingChecklist
                testId="candidate-onboarding"
                title="Próximos passos"
                subtitle="Completa estes passos para tirar o máximo partido do First2Job."
                items={onboardingItems}
              />

              <div className="grid gap-6 lg:grid-cols-2">
                <ApplicationsChart weeks={8} />
                <UpcomingInterviews onOpenPrep={setPrepInterview} />
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                <RecommendationsSection limit={6} onExploreAll={() => navigate("/empregos")} onCount={setMatchesCount} />
                <ActivityFeed
                  apps={apps} saved={saved} alerts={alerts}
                  onOpenApp={(a) => openLetter(a)}
                  onOpenTab={setActiveTab}
                />
              </div>
            </div>
          )}

          {/* APPLICATIONS TAB */}
          {activeTab === "applications" && (
            <ApplicationsList
              apps={apps}
              generatingLetter={generatingLetter}
              onOpenLetter={openLetter}
              onOpenJob={(slug) => navigate(`/empregos/${slug}`)}
              onBrowseJobs={() => navigate("/empregos")}
            />
          )}

          {/* RECOMMENDATIONS TAB */}
          {activeTab === "recommendations" && (
            <RecommendationsSection limit={20} onExploreAll={() => navigate("/empregos")} />
          )}

          {/* SAVED TAB */}
          {activeTab === "saved" && (
            saved.length === 0 ? (
              <EmptyState icon={Heart} title="Sem vagas guardadas" description="Guarda vagas para veres mais tarde."
                actionLabel="Explorar vagas" onAction={() => navigate("/empregos")} />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{saved.map((j) => <JobCard key={j.id} job={j} variant="grid" />)}</div>
            )
          )}

          {/* ALERTS TAB */}
          {activeTab === "alerts" && <AlertsTab />}

          {/* SAVED SEARCHES TAB */}
          {activeTab === "searches" && <SavedSearchesTab />}

          {activeTab === "notifications" && <NotificationsTab />}

          {/* PROFILE TAB */}
          {activeTab === "profile" && (
            <div className="max-w-2xl space-y-5">
              <CvSection onProfileParsed={(p) => setProfile((prev) => ({
                ...prev, name: p.name || "", phone: p.phone || "", district: p.district || "",
                headline: p.headline || "", about: p.about || "",
              }))} />
              <ReferralCard role="candidate" />
              <ProfileForm profile={profile} setProfile={setProfile} meta={meta} onSubmit={saveProfile} />
              <LinkedinImportCard
                importing={linkedinImporting}
                preview={linkedinPreview}
                onFileChange={handleLinkedinImport}
                onApply={applyLinkedinImport}
                onCancel={() => setLinkedinPreview(null)}
              />
              <ProfileOptimizer optimizing={optimizing} suggestions={suggestions} onAnalyze={optimizeProfile} />
              <AccountSection />
            </div>
          )}
        </main>
      </div>

      <Footer />

      <CoverLetterDialog
        letter={letterDialog}
        onClose={() => setLetterDialog(null)}
        copied={copied}
        onCopy={copyLetter}
        onRegenerate={regenLetter}
        generating={generatingLetter === letterDialog?.appId}
      />

      <InterviewPrepDialog
        interview={prepInterview}
        onClose={() => setPrepInterview(null)}
      />
    </div>
  );
}
